import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BrandLogo } from "@/components/BrandLogo";
import { toast } from "sonner";

export default function Auth() {
  const { user, login, register, googleSession } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [reg, setReg] = useState({ name: "", email: "", password: "" });
  const [log, setLog] = useState({ email: "", password: "" });

  // Handle Emergent Google auth redirect (session_id in URL hash)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
      if (sid) {
        setLoading(true);
        googleSession(sid)
          .then(() => { toast.success("Signed in with Google"); navigate("/account"); })
          .catch(() => toast.error("Google sign-in failed"))
          .finally(() => { setLoading(false); window.history.replaceState(null, "", "/auth"); });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user && !window.location.hash) navigate("/account");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const googleLogin = () => {
    const redirect = `${window.location.origin}/auth`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(log.email, log.password);
      toast.success("Welcome back");
      navigate("/account");
    } catch {
      toast.error("Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(reg.name, reg.email, reg.password);
      toast.success("Account created");
      navigate("/account");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title="Sign In | Just Peptides Supply" path="/auth" noindex />
      <section className="mx-auto max-w-md px-5 py-20">
        <div className="text-center mb-8 flex flex-col items-center">
          <BrandLogo h={88} />
          <p className="mt-4 text-brand-navy/55 text-sm">Access your orders and consultations</p>
        </div>

        <div className="rounded-2xl border border-brand-navy/10 bg-white p-7">
          <Button onClick={googleLogin} variant="outline" className="w-full rounded-full h-12 gap-2" data-testid="google-login-button">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
            Continue with Google
          </Button>

          <div className="my-6 flex items-center gap-3 text-brand-navy/30 text-xs font-mono-label uppercase tracking-widest">
            <span className="h-px flex-1 bg-brand-navy/10" /> or <span className="h-px flex-1 bg-brand-navy/10" />
          </div>

          <Tabs defaultValue="login">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="login" data-testid="tab-login">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={doLogin} className="space-y-4 mt-5">
                <div><Label>Email</Label><Input type="email" value={log.email} onChange={(e) => setLog({ ...log, email: e.target.value })} className="mt-1.5" data-testid="login-email" /></div>
                <div><Label>Password</Label><Input type="password" value={log.password} onChange={(e) => setLog({ ...log, password: e.target.value })} className="mt-1.5" data-testid="login-password" /></div>
                <Button type="submit" disabled={loading} className="w-full rounded-full bg-brand-navy hover:bg-brand-blue text-white h-12" data-testid="login-submit">
                  {loading ? "Please wait…" : "Sign In"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={doRegister} className="space-y-4 mt-5">
                <div><Label>Full name</Label><Input value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} className="mt-1.5" data-testid="register-name" /></div>
                <div><Label>Email</Label><Input type="email" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} className="mt-1.5" data-testid="register-email" /></div>
                <div><Label>Password</Label><Input type="password" value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} className="mt-1.5" data-testid="register-password" /></div>
                <Button type="submit" disabled={loading} className="w-full rounded-full bg-brand-navy hover:bg-brand-blue text-white h-12" data-testid="register-submit">
                  {loading ? "Please wait…" : "Create Account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </>
  );
}
